import { Mark } from '@c4ads/c4blocks';
import { saveAs } from 'file-saver';
import posthog from 'posthog-js';
import { useState } from 'react';
import * as XLSX from 'xlsx';

import { downloadSvg } from '../../../helpers/chartHelpers';
import { captureExportChart } from '../../../posthog';
import { CHART_CONTENT_CLASSNAME } from '../../Chart';
import { openSnackBar } from '../../Snackbar';
import { addLegend, addRegularLegend, createSvgWrapper, getChartLabel, svgElementSize } from './svgHelpers';


interface AddLegendInterface {
    svg: SVGElement;
    image: HTMLElement;
    container: HTMLDivElement;
}

interface UseDownloadChartProps {
    data: {
        series: unknown;
        seriesKey: unknown;
        label: unknown;
        data: unknown;
        key: unknown;
    }[];
    fileName: string;
}

interface DownloadAsImageProps {
    ref: React.RefObject<HTMLDivElement>,
    mark: Mark
}

export const useDownloadChart = ({ data, fileName }: UseDownloadChartProps) => {
   const [isLoading, setIsLoading] = useState(false);

    const downloadAsXLS = async () => {
        if (!data.length) {
            openSnackBar({ type: 'error', message: 'No chart data available to download.' });
            return;
        }

        setIsLoading(true);
        const worksheet = XLSX.utils.json_to_sheet(data);
        const workbook = XLSX.utils.book_new();

        XLSX.utils.book_append_sheet(workbook, worksheet, 'DataSheet');

        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

        const blobType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
        const fileBlob = new Blob([excelBuffer], { type: blobType });

        await saveAs(fileBlob, `${fileName || 'export'}.xlsx`);
        setIsLoading(false);
    };

    const downloadAsCSV = () => {
        if (!data.length) {
            openSnackBar({ type: 'error', message: 'No chart data available to download.' });
            return;
        }

        setIsLoading(true);
        const headers = Object.keys(data[0]).join(',');

        const rows = data.map((row) =>
            Object.values(row)
                .map((value) => `"${String(value).replace(/"/g, '""')}"`) // Escape quotes
                .join(',')
        );

        const csvContent = [headers, ...rows].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');

        link.setAttribute('href', url);
        link.setAttribute('download', `${fileName}.csv`);
        link.style.visibility = 'hidden';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        setIsLoading(false);
    };

    const downloadAsImage = async ({ ref, mark }: DownloadAsImageProps) => {
        const block = ref.current;
        if (!block) {
            return;
        }

        const svgWrapper = createSvgWrapper();
        const svgContainer = block.querySelector('svg');
        let svgElement = block.querySelector(`.${CHART_CONTENT_CLASSNAME}`);
        if (!svgElement || !svgContainer) {
            return;
        }
        // Getting inner svg for such components as Line Chart, Bar Chart, Scatter Chart
        if (svgElement.children[0]?.tagName === 'svg') {
            svgElement = svgElement.children[0];
        }

        svgElement = svgElement.cloneNode(true) as Element;
        svgElement.setAttribute('xmlns', 'http://www.w3.org/2000/svg');

        setIsLoading(true);

        const cleanupElements: any[] = [];
        let newSvgElement: AddLegendInterface | undefined;
        if (mark === 'Choropleth') {
            newSvgElement = await addLegend(svgContainer);
            if (newSvgElement) {
                cleanupElements.push(...Object.values(newSvgElement));
            }
        } else {
            // Handle regular legends for other chart types
            newSvgElement = await addRegularLegend(svgContainer);
            if (newSvgElement) {
                cleanupElements.push(...Object.values(newSvgElement));
            }
        }

        const label = await getChartLabel(svgContainer);
        if (label) {
            svgWrapper.appendChild(label.svg);
            cleanupElements.push(...Object.values(label));
        }

        let contentElement = svgElement;
        if (newSvgElement) {
            contentElement = newSvgElement.svg;
        }

        contentElement.setAttribute(
            'y',
            `${
                label?.svg && !contentElement.getAttribute('data-chartconfig')
                    ? svgElementSize(label.svg).height + 16
                    : 0
            }`
        );
        svgWrapper.appendChild(contentElement);

        const { width, height } = Array.from(svgWrapper.childNodes).reduce(
            (acc, item) => {
                const { width, height } = svgElementSize(item as SVGSVGElement);

                return { width, height: acc.height + height };
            },
            { width: 0, height: 0 }
        );

        const gap = 12; // gap between label and chart
        svgWrapper.setAttribute('width', `${width}`);
        svgWrapper.setAttribute('height', `${height + gap}`);

        const result = await downloadSvg(svgWrapper, fileName, 'png');

        const { eventName, payload } = captureExportChart(fileName);
        posthog?.capture(eventName, payload);

        cleanupElements.forEach((element) => {
            element?.remove();
        });

        if (!result) {
            openSnackBar({ type: 'error', message: 'The chart could not be loaded, please try again.' });
        }

        setIsLoading(false);
    };

    return { downloadAsCSV, downloadAsXLS, downloadAsImage, isLoading };
};
